import { useState, useEffect, useMemo } from "react";
import { sqlRead, payloadOf } from "../utils/sqlClient";
import { AddCostCodeFlyout } from "../components/CostCodePicker";
import { divisionLabel } from "../config/csiDivisions";
import { btnPrimary } from "../styles/buttons";

import { useIsAdmin, AdminGate } from "../auth/AdminContext";

// Cost Codes — CostCodes is the single source of truth for CSI structure
// across the app (Quote Log Setup categories, Condition Library types, Room
// Takeoff Scope Register, Estimate Engine catalog rows all anchor to a real
// row here). Until now the only write path was CostCodePicker.jsx's
// "+ Add Cost Code" flyout (insert-only, embedded inside whichever page
// needed a code that didn't exist yet) — there was never a page to browse,
// correct, or deactivate an existing code. This is that page.

const th = { textAlign: "left", fontSize: 11, fontWeight: 600, color: "var(--text3)", padding: "6px 8px", borderBottom: "1px solid var(--cs-border)" };
const td = { padding: "4px 8px", borderBottom: "1px solid var(--cs-border)", fontSize: 12 };
const inputSm = { width: "100%", fontSize: 12, padding: "3px 6px", border: "1px solid var(--cs-border)", borderRadius: 4, boxSizing: "border-box" };

const TIERS = ["Core", "Secondary", "Reference"];

export default function CostCodes() {
  const isAdmin = useIsAdmin();

  const [codes, setCodes]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState("");

  const [search, setSearch]   = useState("");
  const [tierFilter, setTierFilter] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  const [openDivisions, setOpenDivisions] = useState(new Set());
  const [draft, setDraft]     = useState(null);
  const [saving, setSaving]   = useState(false);
  const [showAddNew, setShowAddNew] = useState(false);

  const load = () => {
    setLoading(true);
    setError("");
    return sqlRead("EXEC sp_GetCostCodes @active_only=0")
      .then(rows => setCodes(rows || []))
      .catch(e => setError(e.message || String(e)))
      .finally(() => setLoading(false));
  };
  // NOT useEffect(load, []) — load() returns a Promise; see QuoteLogSetup.jsx's
  // identical comment for the exact blank-page bug this avoids.
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return codes.filter(c => {
      if (!showInactive && !c.IsActive) return false;
      if (tierFilter && c.Tier !== tierFilter) return false;
      if (q && !`${c.CSICode} ${c.Description}`.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [codes, search, tierFilter, showInactive]);

  const byDivision = useMemo(() => {
    const map = new Map();
    for (const c of filtered) {
      const key = c.Division ?? null;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(c);
    }
    return [...map.entries()].sort((a, b) => (a[0] ?? 999) - (b[0] ?? 999));
  }, [filtered]);

  function toggleDivision(key) {
    setOpenDivisions(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  }

  async function saveDraft() {
    if (!draft.CSICode?.trim() || !draft.Description?.trim()) {
      alert("CSI Code and Description are required.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        cost_code_id: draft.CostCodeID, csi_code: draft.CSICode.trim(), description: draft.Description.trim(),
        division: draft.Division === "" || draft.Division == null ? null : +draft.Division,
        csi_level: draft.CSILevel || null, tier: draft.Tier || null,
        estimating_trade: draft.EstimatingTrade || null, bid_package: draft.BidPackage || null,
        default_unit: draft.DefaultUnit || null, is_active: draft.IsActive ? 1 : 0,
      };
      const result = await sqlRead(`EXEC sp_SaveCostCode @payload='${payloadOf(payload)}'`);
      if (!result?.[0]?.Success) { alert(result?.[0]?.ErrorMessage || "Save failed"); return; }
      setDraft(null);
      load();
    } catch (e) { alert("Error saving: " + e.message); }
    finally { setSaving(false); }
  }

  if (isAdmin !== true) return <AdminGate isAdmin={isAdmin} />;
  if (loading) return <div style={{ padding: 32, color: "var(--text3)", fontSize: 13 }}>Loading…</div>;
  if (error) return <div style={{ padding: 32, color: "var(--red)", fontSize: 13 }}>{error}</div>;

  return (
    <div style={{ padding: 32, maxWidth: 1200 }}>
      {showAddNew && (
        <AddCostCodeFlyout onClose={() => setShowAddNew(false)} onSaved={() => load()} />
      )}

      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, marginBottom: 12 }}>
        <div>
          <h2 style={{ margin: "0 0 4px", fontSize: 17, fontWeight: 700, color: "var(--text)" }}>Cost Codes</h2>
          <p style={{ margin: 0, fontSize: 13, color: "var(--text3)" }}>
            The single source of truth for CSI structure across the app — Quote Log Setup categories, Condition
            Library types, the Scope & RACI register, and the Estimate Engine catalog all anchor to a real code here.
          </p>
        </div>
        <button onClick={() => setShowAddNew(true)} style={{ ...btnPrimary, flexShrink: 0 }}>+ Add Cost Code</button>
      </div>

      <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
        <input placeholder="Search code or description…" value={search} onChange={e => setSearch(e.target.value)} style={{ ...inputSm, width: 260 }} />
        <select value={tierFilter} onChange={e => setTierFilter(e.target.value)} style={{ ...inputSm, width: 160 }}>
          <option value="">All tiers</option>
          {TIERS.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <label style={{ fontSize: 12, color: "var(--text2)", display: "flex", alignItems: "center", gap: 6 }}>
          <input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)} />
          Show inactive
        </label>
        <span style={{ fontSize: 11, color: "var(--text3)", alignSelf: "center", marginLeft: "auto" }}>{filtered.length} of {codes.length} codes</span>
      </div>

      <div style={{ border: "1px solid var(--cs-border)", borderRadius: 8, overflow: "hidden" }}>
        {byDivision.map(([division, rows]) => {
          const divOpen = openDivisions.has(division);
          return (
            <div key={division ?? "none"}>
              <div
                onClick={() => toggleDivision(division)}
                style={{
                  display: "flex", alignItems: "center", gap: 6, padding: "8px 10px", cursor: "pointer",
                  fontSize: 12, fontWeight: 700, color: "var(--text2)", background: "#fafbfc",
                  borderTop: "1px solid var(--cs-border)",
                }}
              >
                <span style={{ display: "inline-block", transform: divOpen ? "rotate(90deg)" : "none", transition: "transform 0.12s", fontSize: 9, color: "var(--text3)" }}>▸</span>
                {divisionLabel(division)}
                <span style={{ marginLeft: "auto", fontWeight: 400, color: "var(--text3)" }}>{rows.length}</span>
              </div>
              {divOpen && (
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead><tr>
                    <th style={th}>Code</th><th style={th}>Description</th><th style={th}>Tier</th>
                    <th style={th}>Trade</th><th style={th}>Bid Package</th><th style={th}>Unit</th><th style={th}>Active</th><th style={th}></th>
                  </tr></thead>
                  <tbody>
                    {rows.map(c => (
                      draft?.CostCodeID === c.CostCodeID ? (
                        <tr key={c.CostCodeID} style={{ background: "var(--surface)" }}>
                          <td style={td}><input value={draft.CSICode} onChange={e => setDraft({ ...draft, CSICode: e.target.value })} style={{ ...inputSm, fontFamily: "monospace" }} /></td>
                          <td style={td}><input value={draft.Description} onChange={e => setDraft({ ...draft, Description: e.target.value })} style={inputSm} /></td>
                          <td style={td}>
                            <select value={draft.Tier || ""} onChange={e => setDraft({ ...draft, Tier: e.target.value || null })} style={inputSm}>
                              <option value="">—</option>
                              {TIERS.map(t => <option key={t} value={t}>{t}</option>)}
                            </select>
                          </td>
                          <td style={td}><input value={draft.EstimatingTrade || ""} onChange={e => setDraft({ ...draft, EstimatingTrade: e.target.value })} style={inputSm} /></td>
                          <td style={td}><input value={draft.BidPackage || ""} onChange={e => setDraft({ ...draft, BidPackage: e.target.value })} style={inputSm} /></td>
                          <td style={td}><input value={draft.DefaultUnit || ""} onChange={e => setDraft({ ...draft, DefaultUnit: e.target.value })} style={inputSm} /></td>
                          <td style={td}><input type="checkbox" checked={!!draft.IsActive} onChange={e => setDraft({ ...draft, IsActive: e.target.checked })} /></td>
                          <td style={td}>
                            <span onClick={saveDraft} style={{ cursor: saving ? "default" : "pointer", color: "var(--cs-blue)", fontWeight: 600, marginRight: 8 }}>{saving ? "Saving…" : "Save"}</span>
                            <span onClick={() => setDraft(null)} style={{ cursor: "pointer", color: "var(--text3)" }}>Cancel</span>
                          </td>
                        </tr>
                      ) : (
                        <tr key={c.CostCodeID} style={{ opacity: c.IsActive ? 1 : 0.5 }}>
                          <td style={{ ...td, fontFamily: "monospace", fontWeight: 600 }}>{c.CSICode}</td>
                          <td style={td}>{c.Description}</td>
                          <td style={td}>{c.Tier || "—"}</td>
                          <td style={td}>{c.EstimatingTrade || "—"}</td>
                          <td style={td}>{c.BidPackage || "—"}</td>
                          <td style={td}>{c.DefaultUnit || "—"}</td>
                          <td style={td}>{c.IsActive ? "Yes" : "No"}</td>
                          <td style={td}>
                            <span onClick={() => setDraft({ ...c })} style={{ cursor: "pointer", color: "var(--cs-blue)" }}>Edit</span>
                          </td>
                        </tr>
                      )
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          );
        })}
        {!byDivision.length && <div style={{ padding: 16, fontSize: 12, color: "var(--text3)" }}>No cost codes match this filter.</div>}
      </div>
    </div>
  );
}
